import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { Search, LocateFixed, Loader2, MapPin } from 'lucide-react';
import { getCurrentPosition } from '@/lib/geo';
import { useT } from '@/lib/i18n';

// Interactive picker: tap the map to drop a pin, search a place, or use current location.
// Works in the browser even when device GPS is unavailable (manual pin).
export const MapPicker = ({ value, onChange, onLabel, height = 240, testid = 'map-picker', hideLocate = false, helpText = null, query: queryProp, onQueryChange, searchPlaceholder }) => {
  const t = useT();
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  // Query can be controlled by the parent (so this box doubles as the destination field)
  // or fall back to internal state when used standalone.
  const [queryInternal, setQueryInternal] = useState('');
  const query = queryProp !== undefined ? queryProp : queryInternal;
  const setQuery = (v) => { if (onQueryChange) onQueryChange(v); else setQueryInternal(v); };
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const skipSearchRef = useRef(false);

  const setMarker = (lat, lng, fly = true) => {
    const map = mapRef.current;
    if (!map) return;
    if (!markerRef.current) {
      const icon = L.divIcon({
        className: 'ss-pin',
        html: `<div style="transform:translate(-50%,-100%)"><div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:var(--primary);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div></div>`,
        iconSize: [26, 26], iconAnchor: [0, 0],
      });
      markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
    } else {
      markerRef.current.setLatLng([lat, lng]);
    }
    if (fly) map.setView([lat, lng], Math.max(map.getZoom(), 16), { animate: true });
  };

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const start = value && value.lat ? [value.lat, value.lng] : [14.5995, 120.9842]; // Manila fallback
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: true }).setView(start, value?.lat ? 16 : 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap' }).addTo(map);
    map.on('click', (e) => {
      setMarker(e.latlng.lat, e.latlng.lng, false);
      onChange && onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
      reverseGeocode(e.latlng.lat, e.latlng.lng);
    });
    mapRef.current = map;
    if (value && value.lat) setMarker(value.lat, value.lng, false);
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value && value.lat && mapRef.current) setMarker(value.lat, value.lng, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=16`, { headers: { Accept: 'application/json' } });
      const d = await r.json();
      if (d && d.display_name && onLabel) onLabel(d.display_name.split(',').slice(0, 2).join(', '));
    } catch (err) { console.warn('Reverse geocode failed:', err); }
  };

  const doSearch = async (e) => {
    e && e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runSearch(query);
  };

  const runSearch = async (q) => {
    if (!q || q.trim().length < 3) { setResults([]); setOpen(false); return; }
    setSearching(true); setOpen(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&countrycodes=ph&q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } });
      const d = await r.json();
      setResults(Array.isArray(d) ? d : []);
    } catch (err) { console.warn('Place search failed:', err); setResults([]); }
    finally { setSearching(false); }
  };

  // Autocomplete-as-you-type (debounced to respect Nominatim's usage policy).
  // Only searches while the field is focused, so programmatic updates
  // (favourite places, a chosen suggestion, a map tap) never re-open the dropdown.
  useEffect(() => {
    if (skipSearchRef.current) { skipSearchRef.current = false; return; }
    if (!focused) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 3) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => runSearch(query), 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const choose = (res) => {
    const lat = parseFloat(res.lat), lng = parseFloat(res.lon);
    setMarker(lat, lng, true);
    onChange && onChange({ lat, lng });
    const label = res.display_name.split(',').slice(0, 2).join(', ');
    setOpen(false); setResults([]);
    skipSearchRef.current = true; // don't re-search for the text we just filled in
    setQuery(label); // this box IS the destination field, so keep the readable label
  };

  const locate = async () => {
    setLocating(true);
    try {
      const p = await getCurrentPosition();
      setMarker(p.latitude, p.longitude, true);
      onChange && onChange({ lat: p.latitude, lng: p.longitude });
      reverseGeocode(p.latitude, p.longitude);
    } catch (e) {
      // graceful: keep manual selection available
      console.debug('locate-me failed; manual pin still available:', e);
    } finally { setLocating(false); }
  };

  return (
    <div data-testid={testid}>
      <form onSubmit={doSearch} className="flex gap-2 mb-2">
        <div className="flex-1 flex items-center gap-2 rounded-xl px-3 border min-h-[52px]" style={{ borderColor: focused ? 'var(--primary)' : 'var(--border)', background: 'var(--surface)' }}>
          <Search size={18} style={{ color: 'var(--text-muted)' }} />
          <input
            data-testid="map-picker-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); setTimeout(() => setOpen(false), 200); }}
            placeholder={searchPlaceholder || t('sr_search')}
            className="flex-1 bg-transparent outline-none text-base"
            style={{ color: 'var(--text)' }}
          />
          {searching && <Loader2 size={16} className="animate-spin" style={{ color: 'var(--text-muted)' }} />}
        </div>
        {!hideLocate && (
          <button type="button" onClick={locate} aria-label={t('sr_locate')} data-testid="map-picker-locate-button" className="inline-flex items-center justify-center rounded-xl border min-h-[48px] w-12" style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--primary)' }}>
            {locating ? <Loader2 size={18} className="animate-spin" /> : <LocateFixed size={18} />}
          </button>
        )}
      </form>

      {open && (
        <div className="rounded-xl border mb-2 overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
          {searching && <div className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)' }}>{t('sr_searching')}</div>}
          {!searching && results.length === 0 && <div className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)' }}>{t('sr_no_results')}</div>}
          {results.map((res) => (
            <button key={res.place_id} type="button" onClick={() => choose(res)} data-testid="map-picker-result" className="w-full text-left px-3 py-2 text-sm flex items-start gap-2 border-t first:border-t-0" style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
              <MapPin size={16} style={{ color: 'var(--primary)', marginTop: 2 }} />
              <span>{res.display_name}</span>
            </button>
          ))}
        </div>
      )}

      <div ref={elRef} style={{ height, width: '100%', borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)' }} />
      <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
        <MapPin size={12} /> {helpText || t('sr_tap_map')}
      </p>
    </div>
  );
};
